import { fetchResource } from './resource.ts';
import { Maniiifest } from 'maniiifest';
import { IIIFSearchSnippet } from '../types/index.ts'; // Adjust the import path
import { maxSearchPages } from '../config/appConfig.ts'; // Import the max pages limit
import { createError } from '../errors/structured.ts';
import { logger } from '../utils/logger.ts';

/**
 * Fetch annotations and extract snippets.
 */
export async function searchAnnotations(
  targetUrl: string,
  signal?: AbortSignal,
): Promise<IIIFSearchSnippet[]> {
  let snippets: IIIFSearchSnippet[] = [];
  let nextPageUrl: string | null = targetUrl;
  let pageCount = 0;
  const MAX_PAGES = maxSearchPages || 10;
  const annotationLookup = new Map<string, any>();
  const hits: any[] = [];

  while (nextPageUrl && pageCount < MAX_PAGES) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  let resource;
  try {
    resource = await fetchResource(nextPageUrl, { signal });
  } catch (err: any) {
    // A 400 from the search service indicates the query itself is invalid
    // (e.g. AnnoSearch's minimum keyword length). Surface a recoverable
    // validation error with a readable message for inline display rather
    // than a viewer-level failure.
    if (err?.httpStatus === 400) {
      throw createError('SEARCH_VALIDATION', parseSearchValidationMessage(err.body), {
        cause: err,
        recoverable: true,
      });
    }
    throw err;
  }
    if (resource.data.type !== 'AnnotationPage') {
      throw createError(
        'NETWORK_SEARCH_FETCH',
        `Invalid or empty response received from ${nextPageUrl}`,
        { cause: resource, recoverable: true }
      );
    }

    // First Pass: Build lookup map from top-level items (base annotations with geometry)
    if (resource.data.items && Array.isArray(resource.data.items)) {
      for (const annotation of resource.data.items) {
        if (annotation.id) {
          annotationLookup.set(annotation.id, annotation);
        }
      }
    }

    // Second Pass: Collect search hits from nested annotation pages
    for (const annotationPage of resource.data.annotations || []) {
      const nestedParser = Maniiifest.parseAnnotationPage(annotationPage);
      for (const annotation of nestedParser.iterateAnnotationPageAnnotation()) {
        // Add nested annotations to lookup as well (in case they're referenced)
        if (annotation.id) {
          annotationLookup.set(annotation.id, annotation);
        }
        // A "hit" is an annotation with motivation "highlighting" or "contextualizing"
        // Per IIIF Content Search 2.0 spec: only exact matching for these motivations
        const motivation = annotation.motivation;
        let isSearchResult = false;

        if (typeof motivation === 'string') {
          isSearchResult = motivation === 'highlighting' || motivation === 'contextualizing';
        } else if (Array.isArray(motivation)) {
          isSearchResult = motivation.some(m => {
            const motStr = String(m);
            return motStr === 'highlighting' || motStr === 'contextualizing';
          });
        }

        if (isSearchResult) {
          hits.push(annotation);
        }
      }
    }

    // Get the next page (if applicable)
    const mainParser = Maniiifest.parseAnnotationPage(resource.data);
    const nextPage = mainParser.getAnnotationPage()?.next as any;
    nextPageUrl = typeof nextPage === 'string' ? nextPage : nextPage?.id ?? null;
    pageCount++;
  }

  // Build snippets from all collected hits after processing all pages
  for (const hit of hits) {
    const snippets_from_hit = buildSnippetsFromAnnotation(hit, annotationLookup);
    snippets.push(...snippets_from_hit);
  }

  return snippets;
}

const GENERIC_SEARCH_VALIDATION_MESSAGE = 'This search query is not valid.';

/**
 * Extracts a readable validation message from a 400 search response body.
 * Falls back to a generic message when no usable error/message field exists.
 */
function parseSearchValidationMessage(body: unknown): string {
  if (typeof body !== 'string' || !body.trim()) {
    return GENERIC_SEARCH_VALIDATION_MESSAGE;
  }
  try {
    const parsed = JSON.parse(body);
    const message = parsed?.error ?? parsed?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  } catch {
    // Not JSON; ignore and fall through to the generic message.
  }
  return GENERIC_SEARCH_VALIDATION_MESSAGE;
}


function buildSnippetsFromAnnotation(
  hitAnnotation: any,
  annotationLookup: Map<string, any>,
): IIIFSearchSnippet[] {
  if (!hitAnnotation?.target || hitAnnotation.type !== 'Annotation') return [];

  // Handle array targets - create multiple snippets
  if (Array.isArray(hitAnnotation.target)) {
    const snippets: IIIFSearchSnippet[] = [];
    for (let i = 0; i < hitAnnotation.target.length; i++) {
      const snippet = buildSnippetFromSingleTarget(hitAnnotation, hitAnnotation.target[i], annotationLookup, i);
      if (snippet) {
        snippets.push(snippet);
      }
    }
    return snippets;
  }

  // Handle single target
  const snippet = buildSnippetFromSingleTarget(hitAnnotation, hitAnnotation.target, annotationLookup, 0);
  return snippet ? [snippet] : [];
}

/**
 * Converts a single target from a search result annotation to an IIIFSearchSnippet.
 */
function buildSnippetFromSingleTarget(
  hitAnnotation: any,
  targetObj: any,
  annotationLookup: Map<string, any>,
  targetIndex: number = 0,
): IIIFSearchSnippet | null {
  // The hit's target source is the ID of the base annotation with the geometry.
  const baseAnnotationId = targetObj.source;
  if (typeof baseAnnotationId !== 'string') {
    logger.warn('Search hit has no target source string.', hitAnnotation);
    return null;
  }

  const baseAnnotation = annotationLookup.get(baseAnnotationId);
  if (!baseAnnotation) {
    logger.warn(
      `Could not find base annotation with ID: ${baseAnnotationId} in the lookup map.`,
    );
    return null;
  }

  // --- Extract data primarily from the base annotation ---

  const baseTarget = baseAnnotation.target;
  let canvasTarget = '';

  if (typeof baseTarget === 'string') {
    canvasTarget = baseTarget;
  } else if (baseTarget?.id) {
    canvasTarget = baseTarget.id;
  } else if (baseTarget?.source) {
    canvasTarget =
      typeof baseTarget.source === 'string'
        ? baseTarget.source
        : baseTarget.source.id;
  }

  const cleanCanvasTarget = canvasTarget.split('#')[0];
  if (!cleanCanvasTarget) {
    logger.warn(
      'Could not determine canvasTarget from base annotation:',
      baseAnnotation,
    );
    return null;
  }

  const partOf = getPartOf(baseTarget);

  // --- Extract text snippet from the search result annotation ---

  let prefix, exact, suffix;
  const hitSelector = targetObj.selector;
  if (hitSelector && Array.isArray(hitSelector)) {
    const textSelector = hitSelector.find(
      (sel: any) => sel.type === 'TextQuoteSelector',
    );
    if (textSelector) {
      prefix = textSelector.prefix;
      exact = textSelector.exact;
      suffix = textSelector.suffix;
    }
  } else if (hitSelector?.type === 'TextQuoteSelector') {
    prefix = hitSelector.prefix;
    exact = hitSelector.exact;
    suffix = hitSelector.suffix;
  }

  if (!exact) {
    logger.warn('Could not find TextQuoteSelector on search result target:', targetObj);
    return null;
  }

  // Extract motivation from the base annotation (the actual content annotation),
  // not the hit annotation which always has 'highlighting'/'contextualizing'.
  // Preserve raw type (string or string[]) so consumers can match without string splitting.
  const motivationSource = baseAnnotation.motivation;
  const motivation: string | string[] = Array.isArray(motivationSource)
    ? motivationSource
    : typeof motivationSource === 'string'
      ? motivationSource
      : '';

  // Create unique ID for multiple targets
  const snippetId = targetIndex > 0 ? `${hitAnnotation.id}-${targetIndex}` : hitAnnotation.id;

  return {
    id: snippetId,
    annotationId: baseAnnotation.id,
    motivation,
    prefix,
    exact,
    suffix,
    canvasTarget: cleanCanvasTarget,
    partOf,
    language: targetObj.language || undefined,
  };
}

/**
 * Recursively finds the manifest ID from a `partOf` property.
 */
function getPartOf(target: any): string | undefined {
  if (!target || typeof target !== 'object') return undefined;

  if (target.partOf) {
    if (typeof target.partOf === 'string') {
      return target.partOf;
    }
    if (typeof target.partOf.id === 'string') {
      return target.partOf.id;
    }
    if (Array.isArray(target.partOf)) {
      for (const p of target.partOf) {
        if (typeof p === 'string') return p;
        if (typeof p.id === 'string') return p.id;
      }
    }
  }

  if (target.source) {
    return getPartOf(target.source);
  }

  return undefined;
}
